import React, {Component} from "react"
import {uniqBy} from "lodash"

import Form from "@irati/ui/src/Form"
import {FormattedMessage, getMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {teamSchema} from "@irati-web/fields/team/PublicName"
import Loading from "@irati/ui/src/Loading"
import adminTracking from "@irati-tracking/admin"
import teamService from "@irati/identity/services/teams"
import {Team, TeamAssignment, TeamCategory, User} from "@irati/types"
import authentication from "@irati/auth"
import {Body, Label} from "@irati/ui/src/Typography"
import Input from "@irati/ui/src/FormItem/Input"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import UserSearch from "@irati/ui/src/UserSearch"
import {RouterLink} from "@irati/ui/src/Link"
import Badge from "@irati/ui/src/Badge"
import theme from "@irati/ui/src/global/theme"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import ErrorText from "@irati/ui/src/ErrorText"
import {Select} from "@irati/ui/src/FormItem"
import {SelectOption} from "@irati/ui/src/FormItem/Select"

import {ASSIGN_MEMBERS} from "../../../routes/routeConfig"
import {StyledH4, MarginRightButton, StyledH2} from "./styled"

interface Props extends InjectIntl {
  onSubmit: (team: Team) => void
  onBack: () => void
}

interface State {
  name: string
  submit: Promise<any> | null
  managers: User[]
  users: User[]
  firstStep: boolean
  error: string | null
  team: Team | null
  teamCategory?: string
}

class CreateTeamName extends Component<Props, State> {
  static defaultProps = {
    onBack: null
  }

  state: State = {
    name: "",
    submit: null,
    managers: [],
    users: [],
    firstStep: true,
    error: null,
    team: null,
    teamCategory: undefined
  }

  teamTypesOptions: SelectOption[] = [
    {label: this.props.formatMessage({id: "admin.components.createTeamName.teamTypes.department"}), value: TeamCategory.Department},
    {label: this.props.formatMessage({id: "admin.components.createTeamName.teamTypes.site"}), value: TeamCategory.Site}
  ]

  handleNameChange = (name: string) => {
    this.setState({name})
  }

  handleTeamCreation = () => {
    const {formatMessage} = this.props
    const {managers, teamCategory} = this.state

    const public_name = this.state.name.trim()

    this.setState({error: null})

    const submit = teamService
      .create(
        {
          tenantId: authentication.userTenant().uuid,
          data: {public_name, teamCategory}
        },
        true
      )
      .then((team: Team) => {
        this.setState({firstStep: false, team: team})

        managers.map((user) =>
          teamService
            .addUserToTeam(team.uuid!, user.uuid!, TeamAssignment.Manager)
            .then(() => {
              this.setState({users: this.state.managers})
            })
            .catch((error: PBError) => {
              const message = getMessage(formatMessage, getErrorMessage(error), getErrorValues(error))
              this.setState({error: message})
            })
        )
      })

    this.setState({submit})
    adminTracking.createTeam(public_name)

    return submit
  }

  handleAssignUsers = () => {
    const {onSubmit, formatMessage} = this.props
    const {users, team, managers} = this.state

    const noManagers = users.filter((user) => !managers.find((u) => u.uuid === user.uuid))

    this.setState({error: null})

    noManagers.map((user) =>
      teamService
        .addUserToTeam(team?.uuid!, user.uuid!, TeamAssignment.Basic)
        .then(() => {
          return onSubmit(team!)
        })
        .catch((error: PBError) => {
          const message = getMessage(formatMessage, getErrorMessage(error), getErrorValues(error))
          this.setState({error: message})
        })
    )
  }

  hangleManagersOnChange = (_: any, selectedUsers) => {
    if (selectedUsers?.length > 0) {
      this.setState({managers: selectedUsers})
    } else {
      this.setState({managers: []})
    }
  }

  hangleUsersOnChange = (_: any, selectedUsers) => {
    if (selectedUsers?.length > 0) {
      this.setState({users: uniqBy(selectedUsers.concat(this.state.managers), "uuid")})
    } else {
      this.setState({users: this.state.managers})
    }
  }

  render() {
    const {submit, name, managers, users, firstStep, error, team, teamCategory} = this.state
    const {formatMessage, onBack} = this.props

    return firstStep ? (
      <Flex direction="column" maxWidth="700px">
        <StyledH2>
          <FormattedMessage id="admin.components.createTeamName.headerTwo" />
        </StyledH2>
        <StyledH4 marginTop={3} marginBottom={4}>
          <FormattedMessage id="admin.components.createTeamName.headerFour" />
        </StyledH4>
        <Form form="assign-managers">
          <Flex direction="column" maxWidth="600px">
            <Label large weight="bold" marginTop={2}>
              <FormattedMessage id="platform.createTeamName.form.input.label" />
            </Label>
            <Body small marginTop maxWidth="100%">
              <FormattedMessage id="admin.components.createTeamName.addTeamsDesc" />
            </Body>
            <Input
              name="teamName"
              hideLabel
              type="text"
              placeholder="platform.createTeamName.form.input.placeholder"
              value={name}
              onChange={this.handleNameChange}
              validation={teamSchema(true)}
            />
            {onBack && (
              <MarginRightButton large onClick={onBack}>
                <FormattedMessage id="admin.components.createTeamName.button.back" />
              </MarginRightButton>
            )}
            <Label large weight="bold" marginTop={2}>
              <FormattedMessage id="admin.components.createTeamName.addManagers" />
            </Label>
            <Body small marginTop maxWidth="100%">
              <FormattedMessage id="admin.components.createTeamName.addManagersDesc" />
            </Body>
            <UserSearch
              id="team-managers-selector"
              isMulti
              hideLabel
              onChange={this.hangleManagersOnChange}
              placeholder="platform.fields.selectRecipient.placeholder"
              recipients={managers.map(({uuid}) => uuid)}
            />
            <Label large weight="bold" marginTop={2}>
              <FormattedMessage id="admin.components.createTeamName.addTeamType" />
            </Label>
            <Body small marginTop maxWidth="100%">
              <FormattedMessage id="admin.components.createTeamName.addTeamTypeDesc" />
            </Body>
            <Select
              name="teamCategory"
              hideLabel
              value={teamCategory}
              options={this.teamTypesOptions}
              onChange={(value) => this.setState({teamCategory: value})}
            />
          </Flex>
          <ValidationButton
            id="continueButton"
            primary
            disabled={!(name && teamCategory)}
            load={submit}
            loading={formatMessage({id: "admin.components.createTeamName.validationButton.loading"})}
            onClick={this.handleTeamCreation}>
            <FormattedMessage id="admin.components.createTeamName.validationButton.continue" />
          </ValidationButton>
          <Flex marginTop>{error && <ErrorText>{error}</ErrorText>}</Flex>
        </Form>
        <Loading load={submit} useLoader={false} />
      </Flex>
    ) : (
      <Flex direction="column" maxWidth="750px">
        <StyledH2>
          <FormattedMessage id="admin.components.createTeamName.nextStepTitle" />
        </StyledH2>
        <StyledH4 marginTop={3} marginBottom={4}>
          <FormattedMessage
            id="admin.components.createTeamName.nextStepDesc"
            values={{
              upload: (
                <RouterLink cta to={`${ASSIGN_MEMBERS}/${team?.uuid}`}>
                  <FormattedMessage id="admin.components.createTeamName.nextStepLink" />
                </RouterLink>
              )
            }}
          />
        </StyledH4>
        <Form form="assign-members">
          {users.length - managers.length >= 0 && (
            <Badge
              bgColor={theme.colors.primary2}
              color={theme.colors.greys.white}
              padding={[theme.spacing.half, theme.spacing.oneAndHalf]}>
              <FormattedMessage id="admin.components.createTeamName.selectedMembers" values={{count: users.length - managers.length}} />
            </Badge>
          )}
          <Body small marginTop={2}>
            <FormattedMessage id="admin.components.createTeamName.addMembers" />
          </Body>
          <UserSearch
            id="team-members-selector"
            isMulti
            hideLabel
            onChange={this.hangleUsersOnChange}
            placeholder="platform.fields.selectRecipient.placeholder"
            recipients={users.map(({uuid}) => uuid)}
          />
          <Button primary onClick={this.handleAssignUsers}>
            <FormattedMessage id="admin.components.createTeamName.validationButton.addTeamMembers" />
          </Button>
          <Flex marginTop>{error && <ErrorText>{error}</ErrorText>}</Flex>
        </Form>
      </Flex>
    )
  }
}

export default injectIntl(CreateTeamName)
